// Ported from hotwire-flow (MIT, @hotwirebits/flow) into conek-ui. The engine
// logic is upstream's; identifiers/events/classes follow the conek contract in
// docs/superpowers/specs/2026-09-09-flow-graph-components-design.md §2.
import { Controller } from "@hotwired/stimulus"
import { resolveDropTarget, handleFraction, sideAnchor } from "../../../lib/flow/utils.js"

// Connection handles — the circles on nodes you drag from/to.
export default class extends Controller {
  static values = {
    id: String,
    nodeId: String,
    type: { type: String, default: "source" },
    position: { type: String, default: "right" },
    // conek extra (spec §4.6): false protects the handle from direct removal
    // via flow.removeHandle — enforced by the flow controller.
    deletable: { type: Boolean, default: true }
  }

  static outlets = ["ui--flow"]

  connect() {
    this.element.dataset.handle = this.idValue
    this.element.dataset.handleNode = this.nodeIdValue
    this.element.dataset.handlePosition = this.positionValue

    // Keyboard a11y: the handle markup carries role=button/tabindex=0/
    // aria-label; the Enter/Space/Escape behavior is attached here (same
    // pattern as node_controller.js, whose keydown listener is attached in
    // connect so directly-mounted controllers get it too).
    this._keydownHandler = (e) => this._onKeydown(e)
    this.element.addEventListener("keydown", this._keydownHandler)

    // Valid-target highlight (React Flow parity): while the engine is
    // connecting, hovering a handle that COULD accept the drop marks it
    // .valid-target (flow.css owns the ring). connectionend/cancel clear it
    // in case the gesture ends while the pointer is still over the handle.
    this._enterHandler = () => this._onPointerEnter()
    this._leaveHandler = () => this._onPointerLeave()
    this._connectionEndHandler = () => this._clearValidTarget()
    this.element.addEventListener("pointerenter", this._enterHandler)
    this.element.addEventListener("pointerleave", this._leaveHandler)
    document.addEventListener("ui--flow:connectionend", this._connectionEndHandler)
    document.addEventListener("ui--flow:connectioncancel", this._connectionEndHandler)
  }

  // Null-safe outlet access (same pattern as node_controller._flow): the
  // outlet getter throws after teardown.
  _flow() {
    try { return this.hasUiFlowOutlet ? this.uiFlowOutlet : null } catch (e) { return null }
  }

  _onPointerEnter() {
    const flow = this._flow()
    if (!flow || !flow.isConnecting) return
    if (flow.connectingFrom?.nodeId === this.nodeIdValue) return // same node is vetoed
    if (typeof flow._dropTypeAllowed === "function" && !flow._dropTypeAllowed(this.idValue, this.nodeIdValue)) return
    this.element.classList.add("valid-target")
  }

  _clearValidTarget() { this.element.classList.remove("valid-target") }

  // Leaving a handle drops its hover ring — unless the host's validTargets
  // set marks it as a valid target for the whole drag (U8).
  _onPointerLeave() {
    const flow = this._flow()
    if (flow && flow.isConnecting && typeof flow._validTargetSet === "function" && flow._validTargetSet()?.has(this.idValue)) return
    this._clearValidTarget()
  }

  pointerDown(event) {
    // Mouse-button guard (M3, mirrors node_controller): only primary presses
    // start a connection. A right/middle press must neither begin connecting
    // nor arm the document move/up listeners — preventDefault on pointerdown
    // never suppresses the contextmenu, and a right press released over
    // another handle COMPLETED an edge (right-click edge creation). Return
    // before stopPropagation so the press bubbles to the context-menu path.
    if (event.button !== 0) return
    event.stopPropagation()
    event.preventDefault()
    const flow = this.uiFlowOutlet
    if (!flow) return
    if (!this._connectable()) return

    flow.startConnecting(this.idValue, this.nodeIdValue, this.getAbsolutePosition())

    this._moveHandler = (e) => this._onMove(e)
    this._upHandler = (e) => this._onUp(e)
    document.addEventListener("pointermove", this._moveHandler)
    document.addEventListener("pointerup", this._upHandler)
  }

  // Keyboard connection path (a11y): Enter/Space on a handle starts a
  // connection through the same flow.startConnecting the pointer path uses;
  // Enter on a handle while connecting completes it via the same public
  // endConnecting the pointer drop calls. Escape cancels through the engine's
  // _cancelConnecting (the same cancel the engine's document-level Escape
  // handler runs — a no-op when idle).
  _onKeydown(event) {
    if (event.key === "Escape") {
      this.uiFlowOutlet?._cancelConnecting?.()
      return
    }
    if (event.key !== "Enter" && event.key !== " ") return

    event.preventDefault()
    event.stopPropagation()

    const flow = this.uiFlowOutlet
    if (!flow || !this._connectable()) return

    if (flow.isConnecting) {
      flow.endConnecting(this.idValue, this.nodeIdValue)
    } else {
      flow.startConnecting(this.idValue, this.nodeIdValue, this.getAbsolutePosition())
      // U18: a keyboard user gets no drag feedback — say what is happening.
      if (flow.isConnecting) flow._announce?.("connectStarted", { port: this.element.dataset.handleLabel || this.idValue })
    }
  }

  // conek extra (spec §4.x): a node with connectable: false exposes its
  // handles as decoration only — neither pointer nor keyboard may start or
  // complete a connection on them. The node root is the handle's closest
  // `[data-ui--flow-node]` (the dataset key node_component emits).
  // Canvas-wide gate (U2): a read-only canvas (or a canvas whose
  // nodes_connectable flag is off) makes every handle decoration too — the
  // canvas switch stacks above the per-node flag, mirroring the drag gate in
  // node_controller.
  _connectable() {
    const flow = this._flow()
    if (flow && (!flow.interactive || flow.nodesConnectableValue === false)) return false
    const nodeEl = this.element.closest("[data-ui--flow-node]")
    if (!nodeEl) return true
    return nodeEl.dataset["ui-FlowNodeConnectableValue"] !== "false"
  }

  _onMove(event) {
    const flow = this.uiFlowOutlet
    if (!flow) return
    const rect = flow.element.getBoundingClientRect()
    const x = (event.clientX - rect.left - flow.viewport.x) / flow.viewport.zoom
    const y = (event.clientY - rect.top - flow.viewport.y) / flow.viewport.zoom
    flow.updateConnecting(x, y)
  }

  _onUp(event) {
    const flow = this.uiFlowOutlet
    if (!flow) return

    const target = this._resolveDropTarget(event)
    if (target && target !== this.element) {
      flow.endConnecting(target.dataset.handle, target.dataset.handleNode)
    } else {
      flow.endConnecting()
    }

    document.removeEventListener("pointermove", this._moveHandler)
    document.removeEventListener("pointerup", this._upHandler)
  }

  // Drop tolerance (React Flow connectionRadius parity): a drop that misses
  // every handle element snaps to the nearest other handle whose rect is
  // within flow.connectionRadiusValue screen px. The resolver is shared with
  // the reconnect drop (lib/flow/utils.js resolveDropTarget). validType
  // RESTRICTS the candidate set — it does not rank: a handle whose DECLARED
  // type mismatches the engine's drop gate is excluded from the radius scan
  // outright (unknown-type handles stay eligible; legacy markup carries no
  // declared type). The engine's completion path still validates the pair
  // (type + duplicate gates), so an invalid snap behaves like a miss.
  _resolveDropTarget(event) {
    const flow = this._flow()
    if (!flow) return null
    const validType = typeof flow._validDropType === "function" ? flow._validDropType() : null
    const accept = typeof flow._validTargetAccept === "function" ? flow._validTargetAccept() : null
    return resolveDropTarget(flow, event, this.element, validType, accept)
  }

  getAbsolutePosition() {
    const nodeEl = this.element.closest("[data-ui--flow-node]")
    if (!nodeEl) return { x: 0, y: 0 }

    // Sub-flow nesting (#18): child style.left/top are RELATIVE to the parent
    // node, so a single-level read anchors the connection start off by every
    // ancestor offset. Fold the [data-ui--flow-node] ancestor chain into the
    // position — same walk minimap_controller._ancestorFlowOffset and
    // edge_controller._flowPosition use.
    let nx = parseFloat(nodeEl.style.left) || 0
    let ny = parseFloat(nodeEl.style.top) || 0
    let p = nodeEl.parentElement
    while (p && p.dataset && p.dataset["ui-FlowNode"]) {
      nx += parseFloat(p.style.left) || 0
      ny += parseFloat(p.style.top) || 0
      p = p.parentElement
    }
    const nw = nodeEl.offsetWidth
    const nh = nodeEl.offsetHeight

    return sideAnchor(this.positionValue, nx, ny, nw, nh, handleFraction(this.element))
  }

  markConnected()    { this.element.classList.add("connected") }
  markDisconnected() { this.element.classList.remove("connected") }

  disconnect() {
    document.removeEventListener("pointermove", this._moveHandler)
    document.removeEventListener("pointerup", this._upHandler)
    if (this._keydownHandler) this.element.removeEventListener("keydown", this._keydownHandler)
    if (this._enterHandler) {
      this.element.removeEventListener("pointerenter", this._enterHandler)
      this.element.removeEventListener("pointerleave", this._leaveHandler)
      document.removeEventListener("ui--flow:connectionend", this._connectionEndHandler)
      document.removeEventListener("ui--flow:connectioncancel", this._connectionEndHandler)
    }
  }
}
